import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Circle, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// Custom red pin icon (SVG inline) to match brand and avoid default Leaflet icon issues
const createIcon = (primary: boolean) => {
  const color = primary ? '#C8102E' : '#1A1A1A';
  const size = primary ? 40 : 32;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
      <circle cx="12" cy="10" r="3" fill="white" stroke="none"/>
    </svg>
  `;
  return L.divIcon({
    html: svg,
    className: 'custom-leaflet-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size]
  });
};
export function ServiceAreas() {
  const areas = [{
    name: 'Monterey',
    status: 'Primary Service Area',
    primary: true,
    coords: [36.6002, -121.8947] as [number, number]
  }, {
    name: 'Salinas',
    status: 'Covered',
    coords: [36.6777, -121.6555] as [number, number]
  }, {
    name: 'Carmel',
    status: 'Covered',
    coords: [36.5552, -121.9233] as [number, number]
  }, {
    name: 'Pacific Grove',
    status: 'Covered',
    coords: [36.6177, -121.9166] as [number, number]
  }, {
    name: 'Seaside',
    status: 'Covered',
    coords: [36.6111, -121.8516] as [number, number]
  }, {
    name: 'Marina',
    status: 'Covered',
    coords: [36.6844, -121.8022] as [number, number]
  }, {
    name: 'Watsonville',
    status: 'Covered',
    coords: [36.9102, -121.7569] as [number, number]
  }, {
    name: 'Santa Cruz',
    status: 'Within Range',
    coords: [36.9741, -122.0308] as [number, number]
  }, {
    name: 'Surrounding Areas',
    status: 'Up to 50 Miles'
  }];
  const monterey: [number, number] = [36.6002, -121.8947];
  // 50 miles in meters
  const radiusMeters = 50 * 1609.34;
  const mappedAreas = areas.filter(a => a.coords);
  return <section id="areas" className="py-20 bg-[#1A1A1A] text-white" data-id="element-375">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-376">
        <div className="text-center mb-16" data-id="element-377">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold mb-4" data-id="element-378">
            WE COVER ALL OF{' '}
            <span className="text-[#C8102E]" data-id="element-379">MONTEREY COUNTY</span> AND BEYOND
          </motion.h2>
          <p className="text-xl text-gray-400" data-id="element-380">
            Based in Monterey, CA — Serving a 50-mile radius.
          </p>
        </div>

        {/* Map + Areas Grid */}
        <div className="grid lg:grid-cols-5 gap-8 mb-12" data-id="element-381">
          {/* Map */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-3 rounded-xl overflow-hidden border-2 border-[#C8102E]/40 shadow-2xl relative bg-white/5 h-[400px] lg:h-auto lg:min-h-[500px]" data-id="element-382">
            <MapContainer center={monterey} zoom={9} scrollWheelZoom={false} style={{
            height: '100%',
            width: '100%',
            minHeight: '400px',
            background: '#1A1A1A'
          }} className="z-0" data-id="element-383">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" data-id="element-384" />

              {/* 50-mile coverage circle */}
              <Circle center={monterey} radius={radiusMeters} pathOptions={{
              color: '#C8102E',
              fillColor: '#C8102E',
              fillOpacity: 0.12,
              weight: 2,
              dashArray: '6, 6'
            }} data-id="element-385" />

              {/* Pins */}
              {mappedAreas.map(area => <Marker key={area.name} position={area.coords as [number, number]} icon={createIcon(!!area.primary)} data-id="element-386">
                  <Tooltip direction="top" offset={[0, -30]} opacity={1} permanent={!!area.primary} data-id="element-387">
                    <span className="font-bold" data-id="element-388">{area.name}</span>
                  </Tooltip>
                  <Popup data-id="element-389">
                    <div className="text-center" data-id="element-390">
                      <strong data-id="element-391">{area.name}</strong>
                      <br data-id="element-392" />
                      <span style={{
                    color: area.primary ? '#C8102E' : '#555'
                  }} data-id="element-393">
                        {area.status}
                      </span>
                    </div>
                  </Popup>
                </Marker>)}
            </MapContainer>

            {/* Legend overlay */}
            <div className="absolute bottom-4 left-4 z-[400] bg-[#1A1A1A]/90 backdrop-blur-sm border border-white/10 rounded-lg p-3 text-xs space-y-2 pointer-events-none" data-id="element-394">
              <div className="flex items-center gap-2" data-id="element-395">
                <span className="w-3 h-3 rounded-full bg-[#C8102E]" data-id="element-396"></span>
                <span className="text-white font-semibold" data-id="element-397">Primary Hub</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-398">
                <span className="w-3 h-3 rounded-full bg-[#1A1A1A] border border-white" data-id="element-399"></span>
                <span className="text-white font-semibold" data-id="element-400">Covered City</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-401">
                <span className="w-3 h-[2px] border-t-2 border-dashed border-[#C8102E]" data-id="element-402"></span>
                <span className="text-white font-semibold" data-id="element-403">50-Mile Radius</span>
              </div>
            </div>
          </motion.div>

          {/* Areas List */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 content-start" data-id="element-404">
            {areas.map((area, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.05
          }} className={`flex items-center justify-between p-4 rounded-lg border ${area.primary ? 'bg-[#C8102E]/20 border-[#C8102E]' : 'bg-white/5 border-white/10'}`} data-id="element-405">
                <div className="flex items-center gap-3" data-id="element-406">
                  <MapPin size={20} className={area.primary ? 'text-[#C8102E]' : 'text-gray-400'} data-id="element-407" />
                  <span className="font-bold text-lg" data-id="element-408">{area.name}</span>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${area.primary ? 'bg-[#C8102E] text-white' : 'bg-white/10 text-gray-300'}`} data-id="element-409">
                  {area.status}
                </span>
              </motion.div>)}
          </div>
        </div>

        <div className="text-center bg-white/5 p-8 rounded-xl border border-white/10 inline-block w-full" data-id="element-410">
          <p className="text-xl mb-4" data-id="element-411">Not sure if you're in our range?</p>
          <a href="tel:8312407401" className="inline-flex items-center gap-2 text-[#C8102E] font-bold text-2xl hover:text-red-400 transition-colors" data-id="element-412">
            <Phone size={24} data-id="element-413" />
            Just call. We'll tell you immediately.
          </a>
        </div>
      </div>
    </section>;
}